import { Box, Grid } from '@mui/material'
import useSessionStorage from 'hooks/useSessionStorage'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'
import { setQuickQuote } from 'reducers/bookingsReducer'
import { WizardProps } from './RouteWizard'
import StepTabs from './StepTabs'

type WizardData = {
  hasPreviousStep: boolean
  hasNextStep: boolean
  loading: boolean
  error?: string
  data: any
  clearData: () => void
  clearNoRedirect: () => void
  addData: (data: object) => void
  setLeaveWarning: (showLeaveWarning?: boolean) => void
}

// Exported for use as a compatibility shim in the new booking page
export const wizardContext = createContext<WizardData>({
  hasPreviousStep: false,
  hasNextStep: false,
  loading: false,
  data: {},
  clearData: () => {},
  clearNoRedirect: () => {},
  addData: () => {},
  setLeaveWarning: () => {},
})

export function useWizard(): WizardData {
  return useContext(wizardContext)
}

export default function Wizard({
  steps,
  loading,
  error,
  basePath,
  initialData,
  onClearWizard,
}: WizardProps) {
  const [isLoading, setIsLoading] = useState(true)
  let { path, url } = useRouteMatch()
  let location = useLocation()
  const history = useHistory()
  const [data, setData] = useSessionStorage('wizard', {})
  const dispatch = useDispatch()
  const [leaveWarningOverride, setLeaveWarningOverride] = useState<
    boolean | undefined
  >(undefined)

  const stepForPath = (pathname: string) => {
    if (!pathname.includes(basePath)) {
      return -1
    }

    return steps?.findIndex((step) => {
      return pathname.endsWith(step.path)
    })
  }

  const activeStepIndex = useMemo(() => {
    return stepForPath(location.pathname)
  }, [location, steps])

  const matchedPath = useMemo(() => {
    if (activeStepIndex === -1) {
      return ''
    }

    const currentStep = steps[activeStepIndex]
    const lastIndex = location.pathname.lastIndexOf(currentStep.path)
    return location.pathname.substring(0, lastIndex)
  }, [activeStepIndex, location])

  const shouldShowLeaveWarning = (step?: any) => {
    if (!step) {
      return false
    }

    if (leaveWarningOverride !== undefined) {
      return leaveWarningOverride
    }

    return step.showLeaveWarning !== false
  }

  useEffect(() => {
    if (
      initialData &&
      (!data || Object.keys(data).length <= 0) &&
      activeStepIndex === 0
    ) {
      setData(initialData)
    }

    setIsLoading(false)
  }, [initialData])

  useEffect(() => {
    setLeaveWarningOverride(undefined)
  }, [activeStepIndex])

  useEffect(() => {
    const step = steps[activeStepIndex]
    if (!step) return

    const unblock = history.block((nextLocation): any => {
      const destRoute = nextLocation?.pathname
      const query = new URLSearchParams(nextLocation?.search)
      const stepIndex = stepForPath(destRoute)
      const leavingWizard = stepIndex === -1
      const startingNewWizard = query.get('new')
      const quickQuote = query.get('bookingType') || ''

      if (!leavingWizard && !startingNewWizard) {
        return true
      }

      if (!shouldShowLeaveWarning(step)) {
        setData({})
        return true
      }

      const shouldProceed = window.confirm(
        `Are you sure you want to leave? Progress will be lost`,
      )

      if (!shouldProceed) {
        return false
      }

      setData({})
      if (onClearWizard) {
        onClearWizard()
      }
      if (quickQuote === 'qq') {
        dispatch(setQuickQuote(true))
      } else {
        dispatch(setQuickQuote(false))
      }

      return true
    })

    return () => {
      unblock()
    }
  }, [
    activeStepIndex,
    dispatch,
    history,
    leaveWarningOverride,
    onClearWizard,
    setData,
    steps,
  ])

  useEffect(() => {
    const step = steps[activeStepIndex]
    if (!shouldShowLeaveWarning(step)) {
      return
    }

    const onBeforeUnload = (e: any) => {
      const confirmationMessage = `Are you sure you want to leave? Progress will be lost`
      e.returnValue = confirmationMessage
      return confirmationMessage
    }

    window.addEventListener('beforeunload', onBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [activeStepIndex, leaveWarningOverride, steps])

  useEffect(() => {
    const onUnload = () => {
      setData({})

      if (onClearWizard) {
        onClearWizard()
      }
      dispatch(setQuickQuote(false))
    }

    window.addEventListener('unload', onUnload)

    return () => {
      window.removeEventListener('unload', onUnload)
    }
  }, [])

  const [noRedirect, setNoRedirect] = useState(false)

  useEffect(() => {
    if (activeStepIndex < 1 || stepForPath(window.location.pathname) === -1) {
      return
    }

    if (noRedirect) {
      return
    }

    if (data == null || Object.keys(data).length === 0) {
      history.push(matchedPath)
    }
  }, [matchedPath, data, activeStepIndex, noRedirect])

  useEffect(() => {
    const findConfirmation = steps[activeStepIndex].path === '/confirmation'
    if (findConfirmation) {
      console.log('lastpage')
    }
  }, [activeStepIndex])

  if (isLoading) {
    return null
  }

  return (
    <wizardContext.Provider
      value={{
        hasPreviousStep: activeStepIndex > 0,
        hasNextStep: activeStepIndex < steps.length - 1,
        loading,
        error,
        data,
        clearData: () => {
          setData({})
          if (onClearWizard) {
            onClearWizard()
          }
          dispatch(setQuickQuote(false))
        },
        clearNoRedirect: () => {
          setNoRedirect(true)
          setData({})
          if (onClearWizard) {
            onClearWizard()
          }
          dispatch(setQuickQuote(false))
        },
        addData: (newData) => setData({ ...data, ...newData }),
        setLeaveWarning: setLeaveWarningOverride,
      }}
    >
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <StepTabs steps={steps} activeStep={activeStepIndex} />
        <Switch>
          {steps?.map((step, i) => {
            return (
              <Route key={`${path}-${i}`} path={`${matchedPath}${step.path}`}>
                <Box component="div" sx={{ marginTop: '20px', width: '100%' }}>
                  <step.component
                    initialValues={step.selector(data)}
                    onSuccess={(stepData: object) => {
                      const newData = step.onSuccess?.(data, stepData)
                      setData(newData)
                      if (i < steps.length - 1) {
                        const findConfirmation =
                          steps[i + 1].path === '/confirmation'
                        if (findConfirmation) {
                          history.replace(`${url}${steps[i + 1].path}`)
                        } else {
                          history.push(`${url}${steps[i + 1].path}`)
                        }
                      }
                    }}
                  />
                </Box>
              </Route>
            )
          })}
        </Switch>
      </Grid>
    </wizardContext.Provider>
  )
}
